import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Pencil, Trash2, ScrollText } from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";

const ACTION_LABEL = {
  AI_CREATED_BOOKING: "ИИ создал запись", AI_UPDATED_BOOKING: "ИИ обновил запись", AI_CANCELLED_BOOKING: "ИИ отменил запись",
  AI_RESCHEDULED_BOOKING: "ИИ перенёс запись", AI_CONFIRMED_BOOKING: "ИИ подтвердил запись", AI_SENT_MARKETING_MESSAGE: "ИИ отправил рассылку",
  AI_SENT_FOLLOWUP_MESSAGE: "ИИ отправил WhatsApp-визитку после звонка", CLIENT_CREATED_BOOKING: "Клиент записался на мини-сайте",
  CLIENT_OPTED_OUT: "Клиент отписался (STOP)", CLIENT_CANCELLED_BOOKING: "Клиент отменил запись", USER_CANCELLED_BOOKING: "Салон отменил запись",
  USER_CORRECTED_CLIENT: "Данные клиента исправлены", USER_DELETED_CLIENT: "Данные клиента удалены", USER_EXPORTED_CLIENT: "Экспорт данных клиента",
};
const ACTOR_CLS = { ai: "bg-purple-100 text-purple-700", user: "bg-blue-100 text-blue-700", client: "bg-green-100 text-green-700", system: "bg-gray-100 text-gray-700" };

// GDPR tools for one client: export, correction, erasure + audit trail of AI/user actions
export default function ClientDataTools({ client, onChanged }) {
  const { t } = useTranslation();
  const tf = (k, fb) => { const v = t(k); return v && v !== k ? v : fb; };
  const key = client.phone_key;
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(client.client_name || "");
  const [phone, setPhone] = useState(client.phones?.[0] || "");

  useEffect(() => {
    axios.get(`${API}/clients/${key}/audit`).then((r) => setAudit(r.data)).catch(() => setAudit({ events: [] }));
  }, [key]);

  const exportData = async () => {
    setBusy("export");
    try {
      const r = await axios.get(`${API}/clients/${key}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `client-${key}-export.json`; a.click();
      URL.revokeObjectURL(a.href);
      toast.success(tf("clients.exported", "Данные клиента выгружены (JSON)"));
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(""); }
  };

  const saveCorrection = async () => {
    setBusy("edit");
    try {
      const r = await axios.put(`${API}/clients/${key}`, { client_name: name, phone });
      toast.success(tf("clients.corrected", "Данные клиента исправлены"));
      setEditing(false);
      onChanged?.("corrected", r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(""); }
  };

  const erase = async () => {
    if (!window.confirm(tf("clients.deleteConfirm", "Удалить данные клиента? Переписка, контакты и согласия будут удалены, записи — обезличены. Это действие необратимо."))) return;
    setBusy("delete");
    try {
      const r = await axios.delete(`${API}/clients/${key}`);
      toast.success(tf("clients.deleted", "Данные клиента удалены"));
      onChanged?.("deleted", r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(""); }
  };

  const consent = audit?.marketing_consent;
  return (
    <div className="my-3 space-y-3" data-testid={`client-tools-${key}`}>
      <div className="flex flex-wrap gap-2">
        <button onClick={exportData} disabled={!!busy} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border-2 border-gray-300 hover:border-black disabled:opacity-50" data-testid={`client-export-${key}`}>
          <Download size={14} /> {tf("clients.export", "Экспорт")}
        </button>
        <button onClick={() => setEditing((v) => !v)} disabled={!!busy} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border-2 border-gray-300 hover:border-black disabled:opacity-50" data-testid={`client-edit-${key}`}>
          <Pencil size={14} /> {tf("clients.correct", "Исправить")}
        </button>
        <button onClick={erase} disabled={!!busy} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border-2 border-red-200 text-red-700 hover:border-red-600 disabled:opacity-50" data-testid={`client-delete-${key}`}>
          <Trash2 size={14} /> {tf("clients.deleteData", "Удалить данные")}
        </button>
        {consent && (
          <span className={`text-xs px-2.5 py-1.5 rounded-lg ${consent.consent && !consent.withdrawn_at ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-600"}`} data-testid={`client-consent-${key}`}>
            {consent.consent && !consent.withdrawn_at ? tf("clients.marketingYes", "Маркетинг: согласие есть") : tf("clients.marketingNo", "Маркетинг: отписан(а)")}
          </span>
        )}
      </div>
      {editing && (
        <div className="flex flex-wrap gap-2 items-end bg-gray-50 p-3 rounded-lg border-2 border-gray-200" data-testid={`client-edit-form-${key}`}>
          <label className="text-xs"><span className="block text-gray-500 mb-1">{tf("clients.name", "Имя")}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className="border-2 border-gray-300 rounded px-2 py-1.5 text-sm" data-testid={`client-edit-name-${key}`} /></label>
          <label className="text-xs"><span className="block text-gray-500 mb-1">{tf("clients.phone", "Телефон")}</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="border-2 border-gray-300 rounded px-2 py-1.5 text-sm" data-testid={`client-edit-phone-${key}`} /></label>
          <button onClick={saveCorrection} disabled={busy === "edit"} className="text-xs font-semibold px-3 py-2 rounded-lg bg-black text-white disabled:opacity-50" data-testid={`client-edit-save-${key}`}>
            {tf("common.save", "Сохранить")}
          </button>
        </div>
      )}
      <div data-testid={`client-audit-${key}`}>
        <p className="font-bold text-sm mb-1.5 inline-flex items-center gap-1.5"><ScrollText size={14} /> {tf("clients.auditTitle", "Журнал действий")}</p>
        {!audit ? <p className="text-xs text-gray-400">…</p> : audit.events.length === 0 ? (
          <p className="text-xs text-gray-400">{tf("clients.auditEmpty", "Пока нет записей")}</p>
        ) : (
          <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
            {audit.events.map((ev) => (
              <div key={ev.id} className="flex items-center gap-2 text-xs p-1.5 bg-gray-50 rounded" data-testid={`audit-event-${ev.id}`}>
                <span className={`px-1.5 py-0.5 rounded font-semibold uppercase ${ACTOR_CLS[ev.actor] || ACTOR_CLS.system}`}>{ev.actor}</span>
                <span className="flex-1 min-w-0 truncate">{ACTION_LABEL[ev.action] || ev.action}{ev.channel ? ` · ${ev.channel}` : ""}</span>
                <span className="text-gray-400 shrink-0">{new Date(ev.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
